import {
  BUY_TOOL_NAME,
  GOVERNED_AGENTS,
  INTAKE_TOOL_NAMES,
  PLAN_TOOL_NAMES
} from '@blisspointmedia/bpm-types';
import {
  AgentCustomEvent,
  AgentResponseSegment,
  AgentToolCallSegment
} from '../../utils/agent-client';
import { PendingAgentOrder } from '../AgentOrderApproval/agent-order-approval-constants';

// A real backend AuditEvent (app/agent/governance/audit.py), delivered over the
// AG-UI `governance.audit` CUSTOM event. This interpretation lives in the Elevate
// demo (not the shared agent transport, which stays domain-agnostic). Field names
// mirror the backend JSON (snake_case → camel).
export interface GovernanceAuditEvent {
  agentId: string;
  action: string;
  status: string;
  sessionId?: string;
  inputs?: Record<string, unknown>;
  outputs?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  timestamp?: string;
}

// Narrow a raw AG-UI custom event into a governance AuditEvent, or null if it is
// not one. The demo passes agent-client's generic `onCustomEvent` output through
// here — keeping the "governance.audit" contract owned by the demo, not the API.
export const governanceAuditFromCustomEvent = (
  event: AgentCustomEvent
): GovernanceAuditEvent | null => {
  if (event.name !== 'governance.audit' || !event.value || typeof event.value !== 'object') {
    return null;
  }
  const v = event.value as Record<string, unknown>;
  return {
    agentId: String(v.agent_id ?? ''),
    action: String(v.action ?? ''),
    status: String(v.status ?? ''),
    sessionId: typeof v.session_id === 'string' ? v.session_id : undefined,
    inputs: (v.inputs as Record<string, unknown>) ?? {},
    outputs: (v.outputs as Record<string, unknown>) ?? {},
    metadata: (v.metadata as Record<string, unknown>) ?? {},
    timestamp: typeof v.timestamp === 'string' ? v.timestamp : undefined
  };
};

// The audit trail, reconstructed for the UI. Forrester wants governance shown
// while the agent runs, not asserted. Until the agent emits its real AuditEvents
// to the frontend (governance.audit CustomEvent — a backend follow-up), we derive
// the feed from REAL signals already on the client: the ordered tool-call stream
// (including the agent's own record_gate_decision calls) and live order state.
// The scope-checks shown are the genuine require-scope gates each governed step
// performs before it runs — so this is honest evidence, not authored copy. Every
// row is tagged with its source so the feed reads as "derived from the tool-call
// sequence," and upgrades to byte-identical-to-CloudWatch when the backend lands.

export const AuditKind = {
  SCOPE_CHECK: 'scope-check',
  AGENT: 'agent',
  TOOL: 'tool',
  GATE: 'gate',
  GUARDRAIL: 'guardrail',
  ORDER: 'order'
} as const;
export type AuditKindType = (typeof AuditKind)[keyof typeof AuditKind];

// 'backend' = a real AuditEvent streamed from the governed runtime (byte-identical
// to CloudWatch); 'derived' = reconstructed from the client-side tool-call stream +
// order state as a fallback until the backend events arrive.
export const AuditSource = { BACKEND: 'backend', DERIVED: 'derived' } as const;
export type AuditSourceType = (typeof AuditSource)[keyof typeof AuditSource];

export interface AuditStreamEvent {
  id: string;
  seq: number;
  kind: AuditKindType;
  // Governed agent id, or 'human' for gate decisions, or '' for an unattributed tool.
  actor: string;
  label: string;
  detail: string;
  source: AuditSourceType;
}

// Agents that actually dispatch through a real backend governed_step (which runs
// require_scope before emitting its audit event). The derived feed only claims a
// scope-check for these — NOT for the conversational 'intake' identity, which is
// registered but never dispatched as a discrete governed step (so asserting a
// require-scope for it would be fabricated).
const GOVERNED_STEP_AGENTS = new Set(['newton-preflight', 'streaming-planner', 'buying']);

interface AuditInputs {
  segments: AgentResponseSegment[];
  newtonReplayed: boolean;
  orders: PendingAgentOrder[] | undefined;
}

const AGENT_BY_ID = new Map(GOVERNED_AGENTS.map(a => [a.id, a]));

// Which governed agent owns each tool (mirrors the web model / bpm-types lists).
const AGENT_BY_TOOL = new Map<string, string>();
INTAKE_TOOL_NAMES.forEach(name => AGENT_BY_TOOL.set(name, 'intake'));
PLAN_TOOL_NAMES.forEach(name => AGENT_BY_TOOL.set(name, 'streaming-planner'));
AGENT_BY_TOOL.set(BUY_TOOL_NAME, 'buying');

const GATE_TOOL = 'record_gate_decision';
// A human-committed order (bpm-types OrderStatus.CONFIRMED). Kept as a literal so
// the module stays free of the const-enum, which is erased at runtime under the
// test runner.
const COMMITTED_STATUS = 20;

const toolCalls = (segments: AgentResponseSegment[]): AgentToolCallSegment[] =>
  segments.filter((s): s is AgentToolCallSegment => s.type === 'tool_call');

export const deriveAuditEvents = ({
  segments,
  newtonReplayed,
  orders
}: AuditInputs): AuditStreamEvent[] => {
  const events: AuditStreamEvent[] = [];
  const seenAgents = new Set<string>();
  const push = (kind: AuditKindType, actor: string, label: string, detail: string): void => {
    events.push({
      id: `audit-${events.length}`,
      seq: events.length,
      kind,
      actor,
      label,
      detail,
      source: AuditSource.DERIVED
    });
  };

  // Emit the invocation the first time an agent shows activity — and a scope-check
  // ONLY for agents that truly dispatch through a governed step (see
  // GOVERNED_STEP_AGENTS); the conversational intake identity is not one, so we
  // don't assert a require-scope it never performed.
  const emitAgentStart = (agentId: string): void => {
    if (seenAgents.has(agentId)) return;
    seenAgents.add(agentId);
    const agent = AGENT_BY_ID.get(agentId);
    if (!agent) return;
    if (GOVERNED_STEP_AGENTS.has(agentId)) {
      push(
        AuditKind.SCOPE_CHECK,
        agentId,
        `scope-check PASS · require-scope ${agent.scope}`,
        `${agent.name} — least-privilege identity authorized`
      );
    }
    push(AuditKind.AGENT, agentId, `agent.invoke · ${agent.name}`, `owner ${agent.owner}`);
  };

  if (newtonReplayed) {
    emitAgentStart('newton-preflight');
    push(
      AuditKind.AGENT,
      'newton-preflight',
      'blueprint.execute · Bliss Point Audience & Planner pre-flight completed',
      'curated blueprints run; client context pack built'
    );
    push(AuditKind.GATE, 'human', 'gate.decision · Gate 1 — intake confirmed', 'actor: human');
  }

  toolCalls(segments).forEach(call => {
    if (call.name === GATE_TOOL) {
      const gate = String(call.args.gate ?? 'gate');
      const decision = String(call.args.decision ?? 'decided');
      push(AuditKind.GATE, 'human', `gate.decision · ${gate} — ${decision}`, 'actor: human');
      return;
    }
    const owner = AGENT_BY_TOOL.get(call.name);
    if (owner) {
      emitAgentStart(owner);
      const agent = AGENT_BY_ID.get(owner);
      push(
        AuditKind.TOOL,
        owner,
        `tool.call · ${call.name}`,
        agent ? `${agent.name} · scope ${agent.scope}` : 'governed tool'
      );
    } else {
      push(AuditKind.TOOL, '', `tool.call · ${call.name}`, 'agent tool');
    }
  });

  (orders ?? []).forEach(order => {
    emitAgentStart('buying');
    const committed = order.status === COMMITTED_STATUS;
    push(
      AuditKind.ORDER,
      'buying',
      `buy.draft_orders · order #${order.id} (${order.network}) ${committed ? 'committed' : 'drafted'}`,
      committed
        ? 'human-committed · still not sent'
        : 'status draft · held for human review · no send'
    );
    if (committed) {
      push(
        AuditKind.GATE,
        'human',
        `gate.decision · Gate 3 — order #${order.id} committed`,
        'actor: human · send still guarded ($1 cap + kill-switch)'
      );
    }
  });

  if (events.length > 0) {
    push(
      AuditKind.GUARDRAIL,
      'buying',
      'guardrail · send-guard enforced in code',
      'automation orders 403 at send unless human-committed and ≤ $1'
    );
  }

  return events;
};

// A "blocked"/"failed"/"rejected" status is a real DENIAL the backend recorded —
// the proof the trail reflects enforcement, not just success.
const DENIAL_STATUSES = new Set(['blocked', 'failed', 'rejected']);

const detailFor = (e: GovernanceAuditEvent): string => {
  const step = e.metadata?.step;
  const parts: string[] = [];
  if (typeof step === 'string') {
    parts.push(`step ${step}`);
  }
  const outKeys = Object.keys(e.outputs ?? {});
  if (outKeys.length) {
    parts.push(outKeys.map(k => `${k}=${String(e.outputs?.[k])}`).join(', '));
  }
  return parts.join(' · ');
};

// Map the agent's REAL backend AuditEvents (governance.audit CustomEvents) onto the
// feed. Unlike the derived path this observes what actually happened — including
// denials — so nothing here is inferred. Every row is tagged source: 'backend'.
export const mapRealAuditEvents = (events: GovernanceAuditEvent[]): AuditStreamEvent[] => {
  const out: AuditStreamEvent[] = [];
  const scopeChecked = new Set<string>();
  const push = (kind: AuditKindType, actor: string, label: string, detail: string): void => {
    out.push({
      id: `live-${out.length}`,
      seq: out.length,
      kind,
      actor,
      label,
      detail,
      source: AuditSource.BACKEND
    });
  };

  for (const e of events) {
    const agent = AGENT_BY_ID.get(e.agentId);
    const name = agent?.name ?? e.agentId;

    if (e.action === 'gate.decision') {
      const gate = String(e.metadata?.gate ?? 'gate');
      const actor = String(e.metadata?.actor ?? 'human');
      push(AuditKind.GATE, 'human', `gate.decision · ${gate} — ${e.status}`, `actor: ${actor}`);
      continue;
    }
    if (e.action === 'blueprint.execute') {
      push(AuditKind.AGENT, e.agentId, `blueprint.execute · ${name} (${e.status})`, detailFor(e));
      continue;
    }
    if (e.action === 'agent.invoke') {
      // A real 'started' event means the governed step ran require_scope and passed
      // — so this scope-check is observed, not asserted.
      if (e.status === 'started' && agent && !scopeChecked.has(e.agentId)) {
        scopeChecked.add(e.agentId);
        push(
          AuditKind.SCOPE_CHECK,
          e.agentId,
          `scope-check PASS · require-scope ${agent.scope}`,
          `${name} — least-privilege identity authorized`
        );
      }
      if (DENIAL_STATUSES.has(e.status)) {
        push(
          AuditKind.GUARDRAIL,
          e.agentId,
          `agent.invoke · ${name} ${e.status.toUpperCase()}`,
          `${detailFor(e)} — blocked in code, not executed`
        );
      } else {
        push(AuditKind.AGENT, e.agentId, `agent.invoke · ${name} ${e.status}`, detailFor(e));
      }
      continue;
    }
    // Any other recorded action — surface it verbatim rather than drop it.
    push(AuditKind.TOOL, e.agentId, `${e.action} · ${e.status}`, detailFor(e));
  }

  return out;
};
